import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { shiftApi } from '@/services/api';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatMoney } from '@/utils/currency';
import { getApiErrorMessage } from '@/utils/apiError';

type Props = {
  variance: any;
  actionType: 'approve' | 'reject';
  onClose: () => void;
};

export default function VarianceApprovalModal({ variance, actionType, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [showLinking, setShowLinking] = useState(false);
  const [linkedPayments, setLinkedPayments] = useState<string[]>([]);

  const { data: recentPayments = [], isLoading: isLoadingPayments } = useQuery({
    queryKey: ['recentSupplierPayments'],
    queryFn: async () => {
      const res = await shiftApi.getRecentSupplierPayments();
      return res.data.message || [];
    },
    enabled: showLinking && actionType === 'approve',
  });

  const mutation = useMutation({
    mutationFn: (data: { variance: string; approval_remarks: string; linked_payments?: string[] }) => {
      if (actionType === 'approve') {
        return shiftApi.approveVariance(data);
      } else {
        return shiftApi.rejectVariance(data);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pendingVariances'] });
      toast.success(
        actionType === 'approve' 
          ? 'Shift variance approved successfully'
          : 'Shift variance rejected'
      );
      onClose();
    },
    onError: (err: any) => {
      const msg = getApiErrorMessage(err, 'Action failed');
      setError(msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (actionType === 'reject' && !remarks.trim()) {
      setError('Remarks are required for rejection');
      return;
    }

    mutation.mutate({
      variance: variance.name,
      approval_remarks: remarks,
      linked_payments: showLinking ? linkedPayments : [],
    });
  };

  const togglePayment = (name: string) => {
    setLinkedPayments(prev => prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]);
  };

  return (
    <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gray-50 shrink-0">
          <div>
            <h3 className="font-semibold text-gray-950 text-lg">
              {actionType === 'approve' ? 'Approve Shift' : 'Reject Shift'}
            </h3>
            <p className="text-xs text-gray-500">
              Shift: <span className="font-mono">{variance.pos_shift}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-500">Cashier</span>
                <span className="text-sm font-medium">{variance.cashier_name}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-500">Difference</span>
                <span className={`text-sm font-bold ${variance.difference < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                  {formatMoney(variance.difference)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Expected</span>
                <span className="text-sm text-slate-700">{formatMoney(variance.expected)}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-slate-500">Counted</span>
                <span className="text-sm text-slate-700">{formatMoney(variance.counted)}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks {actionType === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                className="input-field min-h-[100px] resize-none"
                placeholder={
                  actionType === 'approve' 
                    ? "Optional notes about this approval..." 
                    : "Reason for rejection..."
                }
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                required={actionType === 'reject'}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-650 p-3.5 rounded-xl text-sm flex items-start gap-2 border border-red-100">
                <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}
            
            {actionType === 'approve' && (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                    checked={showLinking}
                    onChange={(e) => setShowLinking(e.target.checked)}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">Was this shortage due to a Supplier Payment?</p>
                    <p className="text-xs text-slate-500 mt-0.5">Link a recent supplier payment to officially resolve the shortage</p>
                  </div>
                </label>

                {showLinking && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    {isLoadingPayments ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" /> Fetching recent payments...
                      </div>
                    ) : recentPayments.length === 0 ? (
                      <p className="text-sm text-amber-600">No unlinked cash supplier payments found in the last 7 days.</p>
                    ) : (
                      <div className="space-y-2 max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
                        {recentPayments.map((p: any) => (
                          <label key={p.name} className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-lg cursor-pointer hover:border-emerald-300 transition-colors">
                            <input 
                              type="checkbox" 
                              className="mt-1 w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                              checked={linkedPayments.includes(p.name)}
                              onChange={() => togglePayment(p.name)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start mb-0.5">
                                <p className="text-sm font-medium text-slate-900 truncate">{p.party}</p>
                                <p className="text-sm font-bold text-emerald-600 shrink-0">{formatMoney(p.amount)}</p>
                              </div>
                              <p className="text-xs text-slate-500 font-mono">{p.name} &bull; {p.posting_date}</p>
                              {p.remarks && <p className="text-xs text-slate-600 mt-1 truncate">{p.remarks}</p>}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {actionType === 'approve' && !showLinking && (
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-100 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Approving this shift will officially close it and accept the difference. No extra cash ledger entries will be created.
                </p>
              </div>
            )}

          </div>

          <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
            <button
              type="submit"
              disabled={mutation.isPending}
              className={`flex-1 py-2.5 flex items-center justify-center gap-1.5 text-white font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50 ${
                actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {actionType === 'approve' ? 'Approve & Close Shift' : 'Reject Shift'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary py-2.5 px-4">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
